import { subDays } from 'date-fns';

export type DateTimeInitType = Date | string | null;
export interface DateTimeValueReturnType {
	startDate: DateTimeInitType;
	endDate: DateTimeInitType;
	utcStartDate?: string;
	utcEndDate?: string;
}

export interface IDateTimeSelector {
	singleSelector?: boolean;
	label?: string;
	errorMsg?: string;
	onDateTimeChange?: (options: DateTimeValueReturnType) => void;
	onDateTimeFilterClick?: (options: DateTimeValueReturnType) => void;
	loading?: boolean;
}

/**
 * Generates ISO formatted date strings for a date range
 * @param days Number of days to subtract from current date for start date (default: 1)
 * @returns Object containing startDate and endDate as ISO strings
 */
export const getDateRangeISO = (days = 1) => {
	const endDate = new Date();
	const endDateISO = endDate.toISOString();

	const startDate = subDays(endDate, days);
	const startDateISO = startDate.toISOString();

	return {
		startDate,
		endDate,
		utcStartDate: startDateISO,
		utcEndDate: endDateISO,
	};
};

/**
 * Ensures that the provided date value is a Date object.
 *
 * @param {DateTimeInitType} dateValue - The date value to check. Can be a Date object, a string that can be parsed into a Date,
 * a number representing milliseconds since the Unix Epoch, or null/undefined.
 * @returns A Date object. If the input is already a Date, it's returned as is.
 * If the input is null or undefined, returns the current date. Otherwise, attempts to convert the input to a Date.
 *
 * @example
 * // Returns a Date object representing the current date
 * ensureSelectedDateIsDate(null);
 *
 * @example
 * // Returns a new Date object created from the string
 * ensureSelectedDateIsDate('2023-01-15');
 *
 * @example
 * // Returns the same Date object
 * const date = new Date();
 * ensureSelectedDateIsDate(date) === date; // true
 */
export const ensureSelectedDateIsDate = (dateValue: DateTimeInitType) => {
	if (!dateValue) return new Date();
	return dateValue instanceof Date ? dateValue : new Date(dateValue);
};
